import { createSlice, createSelector } from '@reduxjs/toolkit'

// Import task selector for progress calculations
import { selectTasks } from './tasksSlice'

// Import timezone utility for accurate date calculations
import { getTodayInUserTimezone } from '../utils/dateUtils'

// Import modular reducers and thunks
import { 
  fetchAnnualGoals,
  createAnnualGoal,
  updateAnnualGoal,
  deleteAnnualGoal,
  annualGoalsReducers
} from './reducers/annualGoalsReducers'

import {
  fetchQuarterlyGoals,
  createQuarterlyGoal,
  updateQuarterlyGoal,
  deleteQuarterlyGoal,
  quarterlyGoalsReducers
} from './reducers/quarterlyGoalsReducers'

import {
  fetchWeeklyGoals,
  createWeeklyGoal,
  updateWeeklyGoal,
  deleteWeeklyGoal,
  weeklyGoalsReducers
} from './reducers/weeklyGoalsReducers'

import {
  fetchAvailableYears,
  promoteDraftGoal,
  unscheduleGoal,
  cascadeCompleteGoal,
  updateDraftGoalOrder,
  goalsUtilityReducers
} from './reducers/goalsUtilityReducers'

// Initial state - Redux-first pattern with initialized flags
const initialState = {
  annualGoals: [],
  quarterlyGoals: [],
  weeklyGoals: [],
  availableYears: [],
  loading: {
    annual: false,
    quarterly: false,
    weekly: false,
    years: false
  },
  error: {
    annual: null,
    quarterly: null,
    weekly: null,
    years: null
  },
  initialized: {
    annual: false,
    quarterly: false,
    weekly: false,
    years: false
  }
}

// Goals slice
const goalsSlice = createSlice({
  name: 'goals',
  initialState,
  reducers: {
    setAnnualGoals: (state, action) => {
      state.annualGoals = action.payload
      state.initialized.annual = true
    },
    setQuarterlyGoals: (state, action) => {
      state.quarterlyGoals = action.payload
      state.initialized.quarterly = true
    },
    setWeeklyGoals: (state, action) => {
      state.weeklyGoals = action.payload
      state.initialized.weekly = true
    },
    clearGoals: (state) => {
      state.annualGoals = []
      state.quarterlyGoals = []
      state.weeklyGoals = []
      state.availableYears = []
      state.initialized = {
        annual: false,
        quarterly: false,
        weekly: false,
        years: false
      }
    },
    clearError: (state, action) => {
      const { goalType } = action.payload
      if (state.error[goalType]) {
        state.error[goalType] = null
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Annual Goals reducers from modular file
      .addCase(fetchAnnualGoals.pending, annualGoalsReducers[fetchAnnualGoals.pending])
      .addCase(fetchAnnualGoals.fulfilled, annualGoalsReducers[fetchAnnualGoals.fulfilled])
      .addCase(fetchAnnualGoals.rejected, annualGoalsReducers[fetchAnnualGoals.rejected])
      .addCase(createAnnualGoal.fulfilled, annualGoalsReducers[createAnnualGoal.fulfilled])
      .addCase(createAnnualGoal.rejected, annualGoalsReducers[createAnnualGoal.rejected])
      .addCase(updateAnnualGoal.fulfilled, annualGoalsReducers[updateAnnualGoal.fulfilled])
      .addCase(updateAnnualGoal.rejected, annualGoalsReducers[updateAnnualGoal.rejected])
      .addCase(deleteAnnualGoal.fulfilled, annualGoalsReducers[deleteAnnualGoal.fulfilled])
      .addCase(deleteAnnualGoal.rejected, annualGoalsReducers[deleteAnnualGoal.rejected])

      // Quarterly Goals reducers from modular file
      .addCase(fetchQuarterlyGoals.pending, quarterlyGoalsReducers[fetchQuarterlyGoals.pending])
      .addCase(fetchQuarterlyGoals.fulfilled, quarterlyGoalsReducers[fetchQuarterlyGoals.fulfilled])
      .addCase(fetchQuarterlyGoals.rejected, quarterlyGoalsReducers[fetchQuarterlyGoals.rejected])
      .addCase(createQuarterlyGoal.fulfilled, quarterlyGoalsReducers[createQuarterlyGoal.fulfilled])
      .addCase(createQuarterlyGoal.rejected, quarterlyGoalsReducers[createQuarterlyGoal.rejected])
      .addCase(updateQuarterlyGoal.fulfilled, quarterlyGoalsReducers[updateQuarterlyGoal.fulfilled])
      .addCase(updateQuarterlyGoal.rejected, quarterlyGoalsReducers[updateQuarterlyGoal.rejected])
      .addCase(deleteQuarterlyGoal.fulfilled, quarterlyGoalsReducers[deleteQuarterlyGoal.fulfilled])
      .addCase(deleteQuarterlyGoal.rejected, quarterlyGoalsReducers[deleteQuarterlyGoal.rejected])

      // Weekly Goals reducers from modular file
      .addCase(fetchWeeklyGoals.pending, weeklyGoalsReducers[fetchWeeklyGoals.pending])
      .addCase(fetchWeeklyGoals.fulfilled, weeklyGoalsReducers[fetchWeeklyGoals.fulfilled])
      .addCase(fetchWeeklyGoals.rejected, weeklyGoalsReducers[fetchWeeklyGoals.rejected])
      .addCase(createWeeklyGoal.fulfilled, weeklyGoalsReducers[createWeeklyGoal.fulfilled])
      .addCase(createWeeklyGoal.rejected, weeklyGoalsReducers[createWeeklyGoal.rejected])
      .addCase(updateWeeklyGoal.fulfilled, weeklyGoalsReducers[updateWeeklyGoal.fulfilled])
      .addCase(updateWeeklyGoal.rejected, weeklyGoalsReducers[updateWeeklyGoal.rejected])
      .addCase(deleteWeeklyGoal.fulfilled, weeklyGoalsReducers[deleteWeeklyGoal.fulfilled])
      .addCase(deleteWeeklyGoal.rejected, weeklyGoalsReducers[deleteWeeklyGoal.rejected])

      // Goals utility reducers from modular file
      .addCase(fetchAvailableYears.pending, goalsUtilityReducers[fetchAvailableYears.pending])
      .addCase(fetchAvailableYears.fulfilled, goalsUtilityReducers[fetchAvailableYears.fulfilled])
      .addCase(fetchAvailableYears.rejected, goalsUtilityReducers[fetchAvailableYears.rejected])
      .addCase(promoteDraftGoal.fulfilled, goalsUtilityReducers[promoteDraftGoal.fulfilled])
      .addCase(promoteDraftGoal.rejected, goalsUtilityReducers[promoteDraftGoal.rejected])
      .addCase(updateDraftGoalOrder.fulfilled, goalsUtilityReducers[updateDraftGoalOrder.fulfilled])
      .addCase(updateDraftGoalOrder.rejected, goalsUtilityReducers[updateDraftGoalOrder.rejected])
      .addCase(unscheduleGoal.fulfilled, goalsUtilityReducers[unscheduleGoal.fulfilled])
      .addCase(unscheduleGoal.rejected, goalsUtilityReducers[unscheduleGoal.rejected])
      .addCase(cascadeCompleteGoal.fulfilled, goalsUtilityReducers[cascadeCompleteGoal.fulfilled])
      .addCase(cascadeCompleteGoal.rejected, goalsUtilityReducers[cascadeCompleteGoal.rejected])
  }
})

// Export all thunks from modular reducers
export { 
  // Annual goals thunks
  fetchAnnualGoals, createAnnualGoal, updateAnnualGoal, deleteAnnualGoal,
  // Quarterly goals thunks
  fetchQuarterlyGoals, createQuarterlyGoal, updateQuarterlyGoal, deleteQuarterlyGoal,
  // Weekly goals thunks
  fetchWeeklyGoals, createWeeklyGoal, updateWeeklyGoal, deleteWeeklyGoal,
  // Utility thunks
  fetchAvailableYears, promoteDraftGoal, unscheduleGoal, cascadeCompleteGoal, updateDraftGoalOrder
}

export const {
  setAnnualGoals,
  setQuarterlyGoals,
  setWeeklyGoals,
  clearGoals,
  clearError
} = goalsSlice.actions

// Selectors
export const selectAnnualGoals = (state) => state.goals.annualGoals
export const selectAllAnnualGoals = (state) => state.goals.annualGoals
export const selectQuarterlyGoals = (state) => state.goals.quarterlyGoals
export const selectWeeklyGoals = (state) => state.goals.weeklyGoals
export const selectAvailableYears = (state) => state.goals.availableYears
export const selectGoalsLoading = (state) => state.goals.loading
export const selectGoalsError = (state) => state.goals.error
export const selectGoalsInitialized = (state) => state.goals.initialized

// Enhanced selectors for filtering
export const selectAnnualGoalsByYear = createSelector(
  [state => state.goals.annualGoals, (state, year) => year],
  (annualGoals, year) => annualGoals.filter(goal => goal.target_year === year)
)

// Draft goal selectors
export const selectDraftAnnualGoals = createSelector(
  [state => state.goals.annualGoals],
  (annualGoals) => annualGoals
    .filter(goal => goal.status === 'draft')
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
)

export const selectDraftQuarterlyGoals = createSelector(
  [state => state.goals.quarterlyGoals],
  (quarterlyGoals) => quarterlyGoals
    .filter(goal => goal.status === 'draft')
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
)

export const selectDraftWeeklyGoals = createSelector(
  [state => state.goals.weeklyGoals],
  (weeklyGoals) => weeklyGoals
    .filter(goal => goal.status === 'draft')
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
)

export const selectAllDraftGoals = createSelector(
  [selectDraftAnnualGoals, selectDraftQuarterlyGoals, selectDraftWeeklyGoals],
  (annual, quarterly, weekly) => ({
    annual,
    quarterly,
    weekly
  })
)

// Scheduled goal selectors (excludes completed goals)
export const selectScheduledAnnualGoals = createSelector(
  [state => state.goals.annualGoals],
  (annualGoals) => annualGoals.filter(goal => goal.status === 'scheduled' && !goal.completed)
)

export const selectScheduledQuarterlyGoals = createSelector(
  [state => state.goals.quarterlyGoals],
  (quarterlyGoals) => quarterlyGoals.filter(goal => goal.status === 'scheduled' && !goal.completed)
)

export const selectScheduledWeeklyGoals = createSelector(
  [state => state.goals.weeklyGoals],
  (weeklyGoals) => weeklyGoals.filter(goal => goal.status === 'scheduled' && !goal.completed)
)

// Unscheduled selectors for goal planning
export const selectUnscheduledQuarterlyMilestones = createSelector(
  [state => state.goals.quarterlyGoals, state => state.goals.annualGoals],
  (quarterlyGoals, annualGoals) => {
    const scheduledAnnualGoalIds = annualGoals
      .filter(goal => goal.status === 'scheduled')
      .map(goal => goal.id)
    
    return quarterlyGoals
      .filter(qGoal => 
        qGoal.annual_goal_id && 
        scheduledAnnualGoalIds.includes(qGoal.annual_goal_id) &&
        (qGoal.status === 'draft' || !qGoal.quarter || !qGoal.year)
      )
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  }
)

export const selectUnscheduledWeeklyPriorities = createSelector(
  [state => state.goals.weeklyGoals, state => state.goals.quarterlyGoals],
  (weeklyGoals, quarterlyGoals) => {
    const scheduledQuarterlyGoalIds = quarterlyGoals
      .filter(goal => goal.status === 'scheduled' && goal.quarter && goal.year)
      .map(goal => goal.id)
    
    return weeklyGoals
      .filter(wGoal => 
        wGoal.quarterly_goal_id && 
        scheduledQuarterlyGoalIds.includes(wGoal.quarterly_goal_id) &&
        (!wGoal.week_start_date || wGoal.status === 'draft')
      )
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  }
)

// Count selectors for goal metrics
export const selectMilestoneCountsByAnnualGoal = createSelector(
  [state => state.goals.quarterlyGoals, state => state.goals.annualGoals],
  (quarterlyGoals, annualGoals) => {
    const counts = {}
    
    annualGoals.forEach(annualGoal => {
      const milestones = quarterlyGoals.filter(qGoal => qGoal.annual_goal_id === annualGoal.id)
      const scheduled = milestones.filter(milestone => milestone.status === 'scheduled' && milestone.quarter && milestone.year).length
      const unscheduled = milestones.filter(milestone => milestone.status === 'draft' || !milestone.quarter || !milestone.year).length
      
      counts[annualGoal.id] = {
        scheduled,
        unscheduled,
        total: scheduled + unscheduled
      }
    })
    
    return counts
  }
)

export const selectWeeklyPriorityCountsByAnnualGoal = createSelector(
  [state => state.goals.weeklyGoals, state => state.goals.quarterlyGoals, state => state.goals.annualGoals],
  (weeklyGoals, quarterlyGoals, annualGoals) => {
    const counts = {}
    
    annualGoals.forEach(annualGoal => {
      // Find quarterly goals for this annual goal
      const relatedQuarterlyGoals = quarterlyGoals.filter(qGoal => qGoal.annual_goal_id === annualGoal.id)
      const quarterlyGoalIds = relatedQuarterlyGoals.map(qGoal => qGoal.id)
      
      // Find weekly goals for these quarterly goals
      const weeklyPriorities = weeklyGoals.filter(wGoal => 
        wGoal.quarterly_goal_id && quarterlyGoalIds.includes(wGoal.quarterly_goal_id)
      )
      
      const scheduled = weeklyPriorities.filter(priority => 
        priority.status === 'scheduled' && priority.week_start_date
      ).length
      const unscheduled = weeklyPriorities.filter(priority => 
        priority.status === 'draft' || !priority.week_start_date
      ).length
      
      counts[annualGoal.id] = {
        scheduled,
        unscheduled,
        total: scheduled + unscheduled
      }
    })
    
    return counts
  }
)

// Selector for unscheduled milestones grouped by annual goal
export const selectUnscheduledMilestonesByAnnualGoal = createSelector(
  [state => state.goals.quarterlyGoals, state => state.goals.annualGoals],
  (quarterlyGoals, annualGoals) => {
    const groupedMilestones = {}
    
    // Get all annual goals that are scheduled (so they can have unscheduled milestones)
    const scheduledAnnualGoals = annualGoals.filter(goal => goal.status === 'scheduled')
    
    scheduledAnnualGoals.forEach(annualGoal => {
      // Find unscheduled quarterly milestones for this annual goal
      const unscheduledMilestones = quarterlyGoals.filter(qGoal => 
        qGoal.annual_goal_id === annualGoal.id &&
        (qGoal.status === 'draft' || !qGoal.quarter || !qGoal.year)
      )
      
      // Only include annual goals that have unscheduled milestones
      if (unscheduledMilestones.length > 0) {
        groupedMilestones[annualGoal.id] = {
          annualGoal,
          milestones: unscheduledMilestones.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        }
      }
    })
    
    return groupedMilestones
  }
)

// Selector for unscheduled weekly priorities grouped by quarterly goal for a specific quarter/year
export const selectUnscheduledWeeklyPrioritiesForQuarter = createSelector(
  [state => state.goals.weeklyGoals, state => state.goals.quarterlyGoals, (state, quarter, year) => ({ quarter, year })],
  (weeklyGoals, quarterlyGoals, { quarter, year }) => {
    // Find quarterly goals for the specified quarter/year that are scheduled
    const targetQuarterlyGoals = quarterlyGoals.filter(qGoal => 
      qGoal.status === 'scheduled' && 
      qGoal.quarter === quarter && 
      qGoal.year === year
    )
    
    const groupedPriorities = {}
    
    targetQuarterlyGoals.forEach(quarterlyGoal => {
      // Find unscheduled weekly priorities for this quarterly goal
      const unscheduledWeeklyPriorities = weeklyGoals.filter(wGoal => 
        wGoal.quarterly_goal_id === quarterlyGoal.id &&
        (!wGoal.week_start_date || wGoal.status === 'draft')
      )
      
      // Only include quarterly goals that have unscheduled weekly priorities
      if (unscheduledWeeklyPriorities.length > 0) {
        groupedPriorities[quarterlyGoal.id] = {
          quarterlyGoal,
          weeklyPriorities: unscheduledWeeklyPriorities.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        }
      }
    })
    
    return groupedPriorities
  }
)

// ============================================================================
// PROGRESS SELECTORS - Task rollup for goal progress tracking
// ============================================================================

// Calculate task progress for each weekly goal
// Returns: { [weeklyGoalId]: { total, completed, percentage } }
export const selectTaskProgressByWeeklyGoal = createSelector(
  [selectWeeklyGoals, selectTasks],
  (weeklyGoals, tasks) => {
    const progressMap = {}

    weeklyGoals.forEach(goal => {
      const goalTasks = tasks.filter(t => t.weekly_goal_id === goal.id)
      const completedTasks = goalTasks.filter(t => t.completed || t.archived)

      progressMap[goal.id] = {
        total: goalTasks.length,
        completed: completedTasks.length,
        percentage: goalTasks.length > 0
          ? Math.round((completedTasks.length / goalTasks.length) * 100)
          : 0
      }
    })

    return progressMap
  }
)

// Calculate rolled-up task progress for each quarterly goal
// Aggregates all tasks from child weekly goals
// Returns: { [quarterlyGoalId]: { total, completed, percentage, weeklyGoalCount } }
export const selectTaskProgressByQuarterlyGoal = createSelector(
  [selectQuarterlyGoals, selectWeeklyGoals, selectTasks],
  (quarterlyGoals, weeklyGoals, tasks) => {
    const progressMap = {}

    quarterlyGoals.forEach(qGoal => {
      // Get all weekly goals under this quarterly goal
      const childWeeklyIds = weeklyGoals
        .filter(w => w.quarterly_goal_id === qGoal.id)
        .map(w => w.id)

      // Get all tasks under those weekly goals
      const goalTasks = tasks.filter(t =>
        childWeeklyIds.includes(t.weekly_goal_id)
      )
      const completedTasks = goalTasks.filter(t => t.completed || t.archived)

      progressMap[qGoal.id] = {
        total: goalTasks.length,
        completed: completedTasks.length,
        percentage: goalTasks.length > 0
          ? Math.round((completedTasks.length / goalTasks.length) * 100)
          : 0,
        weeklyGoalCount: childWeeklyIds.length
      }
    })

    return progressMap
  }
)

// Calculate rolled-up task progress for each annual goal
// Aggregates all tasks from child quarterly → weekly goals
// Returns: { [annualGoalId]: { total, completed, percentage, quarterlyGoalCount, weeklyGoalCount } }
export const selectTaskProgressByAnnualGoal = createSelector(
  [selectAnnualGoals, selectQuarterlyGoals, selectWeeklyGoals, selectTasks],
  (annualGoals, quarterlyGoals, weeklyGoals, tasks) => {
    const progressMap = {}

    annualGoals.forEach(aGoal => {
      // Get all quarterly goals under this annual goal
      const childQuarterlyIds = quarterlyGoals
        .filter(q => q.annual_goal_id === aGoal.id)
        .map(q => q.id)

      // Get all weekly goals under those quarterly goals
      const childWeeklyIds = weeklyGoals
        .filter(w => childQuarterlyIds.includes(w.quarterly_goal_id))
        .map(w => w.id)

      // Get all tasks under those weekly goals
      const goalTasks = tasks.filter(t =>
        childWeeklyIds.includes(t.weekly_goal_id)
      )
      const completedTasks = goalTasks.filter(t => t.completed || t.archived)

      progressMap[aGoal.id] = {
        total: goalTasks.length,
        completed: completedTasks.length,
        percentage: goalTasks.length > 0
          ? Math.round((completedTasks.length / goalTasks.length) * 100)
          : 0,
        quarterlyGoalCount: childQuarterlyIds.length,
        weeklyGoalCount: childWeeklyIds.length
      }
    })

    return progressMap
  }
)

// Aggregate metrics for a specific year
// Returns overall stats: tasks, goal counts by level
export const selectYearMetrics = createSelector(
  [
    selectAnnualGoals,
    selectQuarterlyGoals,
    selectWeeklyGoals,
    selectTasks,
    (state, year) => year
  ],
  (annualGoals, quarterlyGoals, weeklyGoals, tasks, year) => {
    // Filter to selected year's annual goals (include all statuses)
    const yearAnnualGoals = annualGoals.filter(g => g.target_year === year)
    const yearAnnualGoalIds = yearAnnualGoals.map(g => g.id)

    // Get quarterly goals that belong to this year's annual goals
    const yearQuarterlyGoals = quarterlyGoals.filter(q =>
      yearAnnualGoalIds.includes(q.annual_goal_id)
    )
    const yearQuarterlyGoalIds = yearQuarterlyGoals.map(q => q.id)

    // Get weekly goals that belong to this year's quarterly goals
    const yearWeeklyGoals = weeklyGoals.filter(w =>
      yearQuarterlyGoalIds.includes(w.quarterly_goal_id)
    )
    const yearWeeklyGoalIds = yearWeeklyGoals.map(w => w.id)

    // Get all tasks for this year's weekly goals
    const yearTasks = tasks.filter(t =>
      yearWeeklyGoalIds.includes(t.weekly_goal_id)
    )
    const completedTasks = yearTasks.filter(t => t.completed || t.archived)

    return {
      year,
      annualGoals: {
        total: yearAnnualGoals.length,
        completed: yearAnnualGoals.filter(g => g.completed).length
      },
      quarterlyGoals: {
        total: yearQuarterlyGoals.length,
        completed: yearQuarterlyGoals.filter(g => g.completed).length
      },
      weeklyGoals: {
        total: yearWeeklyGoals.length,
        completed: yearWeeklyGoals.filter(g => g.completed).length
      },
      tasks: {
        total: yearTasks.length,
        completed: completedTasks.length,
        percentage: yearTasks.length > 0
          ? Math.round((completedTasks.length / yearTasks.length) * 100)
          : 0
      }
    }
  }
)

// Metrics grouped by category for a specific year
// Returns: { [category]: { annualGoals, taskTotal, taskCompleted, percentage } }
export const selectCategoryMetricsForYear = createSelector(
  [
    selectAnnualGoals,
    selectQuarterlyGoals,
    selectWeeklyGoals,
    selectTasks,
    (state, year) => year
  ],
  (annualGoals, quarterlyGoals, weeklyGoals, tasks, year) => {
    const categories = {}

    // Filter annual goals to selected year (include all statuses)
    const yearAnnualGoals = annualGoals.filter(g => g.target_year === year)

    yearAnnualGoals.forEach(aGoal => {
      const category = aGoal.category || 'uncategorized'

      if (!categories[category]) {
        categories[category] = {
          annualGoals: [],
          annualGoalsCompleted: 0,
          taskTotal: 0,
          taskCompleted: 0
        }
      }

      categories[category].annualGoals.push(aGoal)
      if (aGoal.completed) {
        categories[category].annualGoalsCompleted++
      }

      // Calculate task progress for this annual goal
      const childQuarterlyIds = quarterlyGoals
        .filter(q => q.annual_goal_id === aGoal.id)
        .map(q => q.id)

      const childWeeklyIds = weeklyGoals
        .filter(w => childQuarterlyIds.includes(w.quarterly_goal_id))
        .map(w => w.id)

      const goalTasks = tasks.filter(t =>
        childWeeklyIds.includes(t.weekly_goal_id)
      )

      categories[category].taskTotal += goalTasks.length
      categories[category].taskCompleted += goalTasks.filter(t => t.completed || t.archived).length
    })

    // Calculate percentages
    Object.keys(categories).forEach(cat => {
      const c = categories[cat]
      c.percentage = c.taskTotal > 0
        ? Math.round((c.taskCompleted / c.taskTotal) * 100)
        : 0
    })

    return categories
  }
)

// ============================================================================
// YEAR PROGRESS SCORE SELECTOR - Weighted composite score for sidebar
// ============================================================================

// Weights for each factor in the year progress score
const SCORE_WEIGHTS = {
  annual: 0.25,
  quarterly: 0.25,
  weekly: 0.20,
  tasks: 0.15,
  pace: 0.10,
  coverage: 0.05
}

// Calculate a weighted year progress score (0-100)
// Factors: goal completion at each level, pace vs time elapsed, goal coverage
export const selectYearProgressScore = createSelector(
  [selectYearMetrics, (state, year) => year],
  (metrics, year) => {
    // Calculate year elapsed percentage using timezone-aware "today"
    const now = getTodayInUserTimezone()
    const yearStart = new Date(year, 0, 1)
    const yearEnd = new Date(year, 11, 31, 23, 59, 59)
    const totalMs = yearEnd - yearStart
    const elapsedMs = Math.max(0, Math.min(totalMs, now - yearStart))
    const yearElapsed = elapsedMs / totalMs

    // Check if we have any goals at all
    const hasAnyGoals = metrics.annualGoals.total > 0 ||
                        metrics.quarterlyGoals.total > 0 ||
                        metrics.weeklyGoals.total > 0

    if (!hasAnyGoals) {
      return {
        score: 0,
        factors: {
          annual: 0,
          quarterly: 0,
          weekly: 0,
          tasks: 0,
          pace: 0,
          coverage: 0
        },
        yearElapsed: Math.round(yearElapsed * 100),
        hasGoals: false
      }
    }

    // Calculate individual factors (0-1 scale)
    const factors = {
      annual: metrics.annualGoals.total > 0
        ? metrics.annualGoals.completed / metrics.annualGoals.total
        : 0,
      quarterly: metrics.quarterlyGoals.total > 0
        ? metrics.quarterlyGoals.completed / metrics.quarterlyGoals.total
        : 0,
      weekly: metrics.weeklyGoals.total > 0
        ? metrics.weeklyGoals.completed / metrics.weeklyGoals.total
        : 0,
      tasks: metrics.tasks.total > 0
        ? metrics.tasks.completed / metrics.tasks.total
        : 0,
      pace: 0,
      coverage: 0
    }

    // Pace factor: are you ahead of schedule? (capped at 1.0, neutral when behind)
    // Uses weighted progress of all goal types
    const weightedProgress = (factors.annual * 0.3) + (factors.quarterly * 0.3) +
                             (factors.weekly * 0.2) + (factors.tasks * 0.2)
    factors.pace = yearElapsed > 0
      ? Math.min(1, weightedProgress / yearElapsed)
      : 0

    // Coverage factor: do you have goals at all levels?
    factors.coverage = (
      (metrics.annualGoals.total > 0 ? 0.4 : 0) +
      (metrics.quarterlyGoals.total > 0 ? 0.35 : 0) +
      (metrics.weeklyGoals.total > 0 ? 0.25 : 0)
    )

    // Weight redistribution for empty goal levels
    // When a level has no goals, redistribute its weight to other levels
    const activeWeights = { ...SCORE_WEIGHTS }
    let redistributeAmount = 0
    const goalLevels = ['annual', 'quarterly', 'weekly', 'tasks']
    const emptyLevels = []

    // Identify empty levels
    if (metrics.annualGoals.total === 0) emptyLevels.push('annual')
    if (metrics.quarterlyGoals.total === 0) emptyLevels.push('quarterly')
    if (metrics.weeklyGoals.total === 0) emptyLevels.push('weekly')
    if (metrics.tasks.total === 0) emptyLevels.push('tasks')

    // Calculate total weight to redistribute
    emptyLevels.forEach(level => {
      redistributeAmount += activeWeights[level]
      activeWeights[level] = 0
    })

    // Redistribute weight to non-empty goal levels only
    const activeLevels = goalLevels.filter(level => !emptyLevels.includes(level))
    if (activeLevels.length > 0 && redistributeAmount > 0) {
      const redistributePerLevel = redistributeAmount / activeLevels.length
      activeLevels.forEach(level => {
        activeWeights[level] += redistributePerLevel
      })
    }

    // Calculate final score
    const score = Math.round(
      Object.entries(factors).reduce((sum, [key, value]) => {
        return sum + (activeWeights[key] * value)
      }, 0) * 100
    )

    return {
      score: Math.max(0, Math.min(100, score)),
      factors,
      yearElapsed: Math.round(yearElapsed * 100),
      hasGoals: true
    }
  }
)

// ============================================================================
// GOAL COMPLETION PROGRESS SELECTORS - For Gantt chart progress bars
// Progress based on child goal completion (not tasks)
// ============================================================================

// Calculate progress for each weekly goal based on completed tasks
// Returns: { [weeklyGoalId]: { total, completed, percentage } }
export const selectWeeklyGoalProgress = createSelector(
  [selectWeeklyGoals, selectTasks],
  (weeklyGoals, tasks) => {
    const progressMap = {}

    weeklyGoals.forEach(goal => {
      const goalTasks = tasks.filter(t => t.weekly_goal_id === goal.id)
      const completedTasks = goalTasks.filter(t => t.completed || t.archived)

      progressMap[goal.id] = {
        total: goalTasks.length,
        completed: completedTasks.length,
        percentage: goalTasks.length > 0
          ? Math.round((completedTasks.length / goalTasks.length) * 100)
          : 0
      }
    })

    return progressMap
  }
)

// Calculate progress for each quarterly goal based on completed weekly goals
// Returns: { [quarterlyGoalId]: { total, completed, percentage } }
export const selectQuarterlyGoalProgress = createSelector(
  [selectQuarterlyGoals, selectWeeklyGoals],
  (quarterlyGoals, weeklyGoals) => {
    const progressMap = {}

    quarterlyGoals.forEach(qGoal => {
      const childWeekly = weeklyGoals.filter(w => w.quarterly_goal_id === qGoal.id)
      const completedWeekly = childWeekly.filter(w => w.completed)

      progressMap[qGoal.id] = {
        total: childWeekly.length,
        completed: completedWeekly.length,
        percentage: childWeekly.length > 0
          ? Math.round((completedWeekly.length / childWeekly.length) * 100)
          : 0
      }
    })

    return progressMap
  }
)

// Calculate progress for each annual goal based on completed quarterly goals
// Returns: { [annualGoalId]: { total, completed, percentage } }
export const selectAnnualGoalProgress = createSelector(
  [selectAnnualGoals, selectQuarterlyGoals],
  (annualGoals, quarterlyGoals) => {
    const progressMap = {}

    annualGoals.forEach(aGoal => {
      const childQuarterly = quarterlyGoals.filter(q => q.annual_goal_id === aGoal.id)
      const completedQuarterly = childQuarterly.filter(q => q.completed)

      progressMap[aGoal.id] = {
        total: childQuarterly.length,
        completed: completedQuarterly.length,
        percentage: childQuarterly.length > 0
          ? Math.round((completedQuarterly.length / childQuarterly.length) * 100)
          : 0
      }
    })

    return progressMap
  }
)

export default goalsSlice.reducer
